import React, { useCallback } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { useIntl } from 'react-intl';

import {
  parseCurrencyAmountFieldValue,
  toCurrencyAmountFieldString,
} from './utils/currency-amount-field-value';

import styles from './currency-amount-step-rail.module.scss';

export interface CurrencyAmountStepRailProps {
  name: NamePath;
  step: number;
  disabled?: boolean;
  min?: number;
  max?: number;
  /**
   * Invoked after the stepped value is written to the form. Use this so consumers
   * (e.g. split charge sync) run the same logic as a typed input `onChange`.
   */
  onAfterValueApplied?: (value: string) => void;
}

export function CurrencyAmountStepRail({
  name,
  step,
  disabled,
  min = 0,
  max,
  onAfterValueApplied,
}: CurrencyAmountStepRailProps): React.ReactElement {
  const intl = useIntl();
  const form = Form.useFormInstance();
  const raw = Form.useWatch(name, form);

  const current = parseCurrencyAmountFieldValue(raw);
  const atMin = current <= min;
  const atMax = max !== undefined && current >= max;

  const applyDelta = useCallback(
    (delta: number) => {
      const nextRaw = Math.round((current + delta) * 100) / 100;
      let next = nextRaw;
      if (next < min) {
        next = min;
      }
      if (max !== undefined && next > max) {
        next = max;
      }
      const nextStr = toCurrencyAmountFieldString(next);
      form.setFieldValue(name, nextStr);
      // setFieldValue does not fire the input's onChange — notify so split totals / peers stay in sync.
      onAfterValueApplied?.(nextStr);
      // setFieldValue does not fire the input's onChange, so validateTrigger rules would not run.
      void form.validateFields([name]).catch(() => undefined);
    },
    [current, form, max, min, name, onAfterValueApplied],
  );

  const handleStepUp = useCallback(() => {
    applyDelta(step);
  }, [applyDelta, step]);

  const handleStepDown = useCallback(() => {
    applyDelta(-step);
  }, [applyDelta, step]);

  return (
    <div
      className={styles.stepperRail}
      role="group"
      aria-label={intl.formatMessage({ id: 'certificates.payment.adjustAmountAria' })}
    >
      <button
        type="button"
        className={styles.stepperButton}
        disabled={disabled || atMax}
        onClick={handleStepUp}
        aria-label={intl.formatMessage({ id: 'certificates.payment.increaseAmountAria' })}
      >
        <UpOutlined className={styles.stepperIcon} />
      </button>
      <button
        type="button"
        className={styles.stepperButton}
        disabled={disabled || atMin}
        onClick={handleStepDown}
        aria-label={intl.formatMessage({ id: 'certificates.payment.decreaseAmountAria' })}
      >
        <DownOutlined className={styles.stepperIcon} />
      </button>
    </div>
  );
}
